import { useMemo } from 'react';
import type { FirestoreError } from 'firebase/firestore';
import type { User } from '@altyor-kronos/shared-types';
import { usePolledResource } from '../../core/data/usePolledResource';
import { apiList } from '../../core/data/restClient';

export interface UsersListState {
  users: Array<{ id: string; data: User }>;
  loading: boolean;
  error: FirestoreError | null;
}

/**
 * Reads the `users` collection — the project wizard's Team step, and the team
 * display on ProjectDetail (which passes `enabled: true` unconditionally).
 *
 * Any authenticated user may list: `firestore.rules:92` allows
 * `read: if isAuthenticated()` and `listScopes.users` mirrors it, because team
 * display needs teammates' profiles regardless of the viewer's role.
 *
 * The previous version of this note claimed the server gate was harmless
 * "because the New project button is gated to privileged roles" — but
 * ProjectDetail.tsx:193 calls this with a hardcoded `true`, so every
 * employee-only account got a silently-swallowed 403 and an empty team display.
 * Only prod, with curated roles, exposed it. See listScopes.ts for the fix.
 */
export function useUsersList(enabled: boolean): UsersListState {
  const { rows, loading, error } = usePolledResource<User & { id: string }>(
    enabled ? 'users' : null,
    () => apiList<User & { id: string }>('/users'),
  );
  // Server list has no orderBy — sort by displayName.
  const users = useMemo(
    () =>
      rows
        .slice()
        .filter((r) => r.active !== false)
        .sort((a, b) => (a.displayName ?? '').localeCompare(b.displayName ?? ''))
        .map((r) => ({ id: r.id, data: r })),
    [rows],
  );
  return { users, loading, error: error as FirestoreError | null };
}
